import { useState, useEffect } from "react";
import { getPlans } from "../apis/subscription";
import { Crown, Check, ArrowRight, Sparkles } from "lucide-react";

const PopularPlanOffer = ({ onPlanClick }) => {
  const [popularPlan, setPopularPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setIsLoading(true);
      const response = await getPlans();
      if (response.data.success) {
        const allPlans = response.data.data;
        const regularPlans = allPlans.filter(p => p.plan_name !== 'trial');
        const popular = regularPlans.find(p => p.is_popular) || regularPlans[1];
        setPopularPlan(popular);
      }
    } catch (error) {
      console.error("Error loading plans:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !popularPlan) {
    return null;
  }

  const monthlyPrice = parseFloat(popularPlan.monthly_price || popularPlan.monthly_amount || 0);
  const yearlyPrice = parseFloat(popularPlan.yearly_price || popularPlan.yearly_amount || 0);
  const yearlyDiscount = parseFloat(popularPlan.yearly_discount || 0);
  
  // Calculate yearly savings
  const monthlyTotal = monthlyPrice * 12;
  const yearlyTotal = yearlyPrice || (monthlyPrice - yearlyDiscount) * 12;
  const savings = monthlyTotal - yearlyTotal;
  const savingsPercent = monthlyPrice > 0 ? Math.round((savings / monthlyTotal) * 100) : 0;
  const monthlyEquivalent = yearlyTotal / 12;

  // Parse features
  const features = (() => {
    try {
      return typeof popularPlan.features === 'string' 
        ? JSON.parse(popularPlan.features || '[]')
        : (Array.isArray(popularPlan.features) ? popularPlan.features : []);
    } catch (e) {
      return [];
    }
  })();

  return (
    <div 
      className="relative h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Mirror Shine Animation Background */}
      <div className="absolute inset-0 rounded-2xl overflow-hidden">
        <div className="absolute inset-0 mirror-shine"></div>
      </div>

      <div
        className="relative rounded-2xl p-5 border-3 shadow-xl hover:shadow-2xl transition-all cursor-pointer h-full flex flex-col overflow-hidden"
        style={{ 
          backgroundColor: "#F9FDF0",
          borderColor: "#EEFA94",
          borderWidth: "3px",
          borderStyle: "solid"
        }}
        onClick={() => onPlanClick && onPlanClick(popularPlan)}
      >
        {/* Tooltip */}
        {isHovered && (
          <div className="absolute right-full top-0 mr-4 w-80 bg-white rounded-xl shadow-2xl border-2 border-gray-200 p-4 z-50 pointer-events-none">
            <div className="absolute right-0 top-6 -mr-2 w-4 h-4 bg-white border-r-2 border-b-2 border-gray-200 transform rotate-45"></div>
            
            {/* Yearly Plan Header */}
            <div 
              className="mb-4 pb-3"
              style={{ borderBottom: "2px solid rgba(238, 250, 148, 0.3)" }}
            >
              <h4 className="text-base font-bold text-gray-900 mb-2">Yearly Plan Details</h4>
              <div 
                className="rounded-lg p-2"
                style={{ backgroundColor: "rgba(238, 250, 148, 0.15)" }}
              >
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-2xl font-bold text-gray-800">
                    ₹{Math.round(monthlyEquivalent).toLocaleString('en-IN')}
                  </span>
                  <span className="text-sm text-gray-600">/month</span>
                </div>
                <p className="text-xs text-gray-600">
                  Billed annually: ₹{Math.round(yearlyTotal).toLocaleString('en-IN')}
                </p>
              </div>
            </div>

            {/* Yearly Savings Highlight */}
            {savings > 0 && (
              <div 
                className="mb-4 p-3 rounded-lg"
                style={{ 
                  backgroundColor: "rgba(238, 250, 148, 0.2)",
                  border: "2px solid rgba(238, 250, 148, 0.5)"
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-700">Yearly Savings:</span>
                  <span className="text-lg font-bold text-gray-800">₹{Math.round(savings).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Discount:</span>
                  <span className="text-sm font-bold text-gray-800">{savingsPercent}% OFF</span>
                </div>
                <div 
                  className="mt-2 pt-2"
                  style={{ borderTop: "1px solid rgba(238, 250, 148, 0.3)" }}
                >
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Monthly Plan:</span>
                    <span className="text-gray-500 line-through">₹{monthlyPrice.toLocaleString('en-IN')}/month</span>
                  </div>
                  <div className="flex items-center justify-between text-xs mt-1">
                    <span className="text-gray-600">Yearly Plan:</span>
                    <span className="font-bold text-gray-800">₹{Math.round(monthlyEquivalent).toLocaleString('en-IN')}/month</span>
                  </div>
                </div>
              </div>
            )}

            {/* Features */}
            {features.length > 0 && (
              <div className="mb-3">
                <h5 className="text-xs font-semibold text-gray-700 mb-2">All Features Included:</h5>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check 
                        className="flex-shrink-0 mt-0.5" 
                        size={14}
                        style={{ color: "#EEFA94" }}
                      />
                      <span className="text-xs text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Yearly Benefits */}
            <div className="pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-600 mb-2">
                <span className="font-semibold text-gray-800">Yearly Plan Benefits:</span>
              </p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li className="flex items-start gap-2">
                  <span style={{ color: "#EEFA94" }}>✓</span>
                  <span>Save {savingsPercent}% compared to monthly billing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: "#EEFA94" }}>✓</span>
                  <span>One payment, full year access</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: "#EEFA94" }}>✓</span>
                  <span>Priority support included</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Popular Badge */}
        <div 
          className="absolute top-3 right-3 px-3 py-1.5 text-gray-900 text-xs font-bold rounded-full z-10 flex items-center gap-1.5 shadow-lg"
          style={{ backgroundColor: "#EEFA94" }}
        >
          <Sparkles size={10} />
          Popular
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full">
          {/* Icon */}
          <div 
            className="p-2.5 rounded-xl w-fit mb-3 shadow-md"
            style={{ backgroundColor: "#EEFA94" }}
          >
            <Crown className="text-gray-900" size={20} />
          </div>

          {/* Plan Name */}
          <h3 className="text-base font-bold text-gray-900 mb-2">
            {popularPlan.plan_display_name || popularPlan.plan_name}
          </h3>

          {/* Yearly Offer Price */}
          <div className="mb-2">
            <div className="flex items-baseline gap-1 mb-0.5">
              <span className="text-2xl font-bold text-gray-900">
                ₹{Math.round(monthlyEquivalent).toLocaleString('en-IN')}
              </span>
              <span className="text-xs text-gray-600">/month</span>
            </div>
            <div className="text-xs text-gray-500 line-through">
              ₹{monthlyPrice.toLocaleString('en-IN')}/month
            </div>
          </div>

          {/* Savings Badge */}
          {savings > 0 && (
            <div 
              className="mb-3 px-2.5 py-1.5 rounded-lg inline-block w-fit"
              style={{ 
                backgroundColor: "rgba(238, 250, 148, 0.3)",
                border: "2px solid rgba(238, 250, 148, 0.6)"
              }}
            >
              <span className="text-gray-800 font-bold text-xs">
                Save {savingsPercent}% ({Math.round(savings).toLocaleString('en-IN')})
              </span>
            </div>
          )}

          {/* Description */}
          {popularPlan.description && (
            <p className="text-xs text-gray-600 mb-3 leading-relaxed flex-1">
              {popularPlan.description}
            </p>
          )}

          {/* CTA Button */}
          <button
            className="w-full py-2.5 px-4 text-gray-900 rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mt-auto"
            style={{ backgroundColor: "#EEFA94" }}
            onMouseEnter={(e) => e.target.style.backgroundColor = "#E0F080"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "#EEFA94"}
            onClick={(e) => {
              e.stopPropagation();
              onPlanClick && onPlanClick(popularPlan);
            }}
          >
            Get Yearly Plan
            <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* CSS for Mirror Shine Animation - Diagonal from bottom-left to top-right */}
      <style>{`
        @keyframes mirrorShine {
          0% {
            transform: translateX(-150%) translateY(150%) rotate(45deg);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateX(150%) translateY(-150%) rotate(45deg);
            opacity: 0;
          }
        }

        .mirror-shine {
          background: linear-gradient(
            135deg,
            transparent 0%,
            transparent 40%,
            rgba(255, 255, 255, 0.8) 50%,
            transparent 60%,
            transparent 100%
          );
          background-size: 200% 200%;
          animation: mirrorShine 3s infinite;
          pointer-events: none;
        }

        .mirror-shine::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            transparent 0%,
            transparent 45%,
            rgba(238, 250, 148, 0.4) 50%,
            transparent 55%,
            transparent 100%
          );
          background-size: 200% 200%;
          animation: mirrorShine 3s infinite 1.5s;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default PopularPlanOffer;
